/**
 * Public options of the embeddable widget and their resolution against the
 * host page (villamine.com hidden inputs, browser language).
 */
import type { VillaCard } from "../domain/api";
import type { Currency } from "../domain/search";
import type { SiteLocale } from "../domain/villa";
import { readHostContact, readHostLanguage, readSiteContext, type HostContact } from "./siteAdapter";

export interface VillamineAIOptions {
  /** Base URL of the Villamine AI API, e.g. "https://ai.villamine.com". Empty = same origin. */
  apiBaseUrl?: string;
  /** Display currency; default: the site's currency switcher, else EUR. */
  currency?: Currency;
  /** Site locale for villa links; default: the site's hiddenDil input, else derived from the conversation language. */
  siteLocale?: SiteLocale;
  /**
   * Initial widget language. Default: the host page's language (villamine.com's hiddenDil, else
   * <html lang>). Never the browser language or location. The visitor's own messages may switch
   * the conversation language afterwards.
   */
  language?: string;
  /** @deprecated use `language`; kept for backward compatibility of the mount options. */
  localeHint?: string;
  /** Element to mount into; default: a new <div id="villamine-ai-root"> appended to <body>, outside any form. */
  container?: HTMLElement;
  /** Isolate styles in a Shadow DOM (default true). */
  shadow?: boolean;
  /** Persist the conversation across page navigations (default "session"). */
  storage?: "session" | "none";
  storageKey?: string;
  defaultOpen?: boolean;
  /** Client-side timeout per API call in milliseconds (default 45000). */
  requestTimeoutMs?: number;
  /**
   * Show verified villa cards with deterministic text as soon as the search is done, then
   * replace the text with the model's translation when it arrives (default true; production
   * configuration from the Phase 2D gate).
   */
  deferReply?: boolean;
  onVillaOpen?: (card: VillaCard, position: number) => void;
  /** AI messages available per browser visitor (product limit, browser-persisted; default 10). */
  messageLimit?: number;
  /**
   * Contact action shown when the allowance is used up. Default: the host page's own WhatsApp
   * (wa.me) or tel: link, read at mount; nothing is hardcoded in the widget.
   */
  contactUrl?: string;
  /** For tests: the document to mount into. */
  document?: Document;
}

export interface ResolvedConfig {
  apiBaseUrl: string;
  currency: Currency;
  siteLocale: SiteLocale | null;
  /** Host page language (base tag such as "de"); null only when the page exposes no language. */
  hostLanguage: string | null;
  storage: "session" | "none";
  storageKey: string;
  defaultOpen: boolean;
  requestTimeoutMs: number;
  deferReply: boolean;
  onVillaOpen: ((card: VillaCard, position: number) => void) | null;
  messageLimit: number;
  contact: HostContact | null;
}

export const DEFAULT_STORAGE_KEY = "villamine-ai:v1";

export function resolveConfig(options: VillamineAIOptions, doc: Document | null | undefined): ResolvedConfig {
  const site = readSiteContext(doc);
  const explicit = (options.language ?? options.localeHint ?? "").toLowerCase().split(/[-_]/)[0];
  const hostLanguage = (/^[a-z]{2,3}$/.test(explicit) ? explicit : null) ?? readHostLanguage(doc);
  return {
    apiBaseUrl: (options.apiBaseUrl ?? "").replace(/\/+$/, ""),
    currency: options.currency ?? site.currency ?? "EUR",
    siteLocale: options.siteLocale ?? site.locale,
    hostLanguage,
    storage: options.storage ?? "session",
    storageKey: options.storageKey ?? (DEFAULT_STORAGE_KEY + (hostLanguage ? ":" + hostLanguage : "")),
    defaultOpen: options.defaultOpen ?? false,
    requestTimeoutMs: options.requestTimeoutMs ?? 45_000,
    deferReply: options.deferReply ?? true,
    onVillaOpen: options.onVillaOpen ?? null,
    messageLimit: Number.isFinite(options.messageLimit) && (options.messageLimit as number) > 0 ? Math.floor(options.messageLimit as number) : 10,
    contact: options.contactUrl ? { url: options.contactUrl, kind: /wa\.me|whatsapp/i.test(options.contactUrl) ? "whatsapp" : options.contactUrl.startsWith("tel:") ? "phone" : "link" } : readHostContact(doc),
  };
}
